import { fetch } from "../lib/network";
import decamelizeKeys from "decamelize-keys";
import decamelizeKeysDeep from "decamelize-keys-deep";
import queryString from "query-string";
import AppSettings from "../config";
import { FetchError } from "../utils/FetchError";
import { Status } from "../features/permits/components/StatusDropdown";
import PermitsService from "../services/PermitsService";
import { buildQuery } from "../lib/network";

export const getPermits = (networkHeaders: any, query?: any) => () =>
  fetch(
    `${AppSettings.PERMITS_API_URL}?${queryString.stringify(decamelizeKeys(query || {}, "_"), {
      arrayFormat: "bracket",
    })}`,
    {
      method: "GET",
      headers: networkHeaders,
    },
  ).then((res) => res.json());

export const getPermit = (networkHeaders: any) => (key: any, id: string) => {
  if (!id) {
    return null;
  }
  return buildQuery(PermitsService.permitsDetail(id, { headers: networkHeaders }), (response) => {
    if (response.status < 200 || response.status > 299) {
      throw new FetchError(response as any);
    }
    return response.data;
  });
};

export const getPermitDisputesInfo = (networkHeaders: any) => () =>
  fetch(`${AppSettings.PERMITS_API_URL}/disputes_info`, {
    method: "GET",
    headers: networkHeaders,
  }).then((res) => res.json());

export const postRequestPayment = (networkHeaders: any, permitId: string) => () =>
  fetch(`${AppSettings.PERMITS_API_URL}/request-payment/${permitId}`, {
    method: "POST",
    headers: networkHeaders,
  })
    // Make sure we handle errors
    .then((res) => {
      if (res.ok) return;
      throw new Error(`Request payment failed with status ${res.status}`);
    });

export const getPermitTransactions = (networkHeaders: any) => (key: any, id: string) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${id}/transactions`, {
    method: "GET",
    headers: networkHeaders,
  }).then((res) => res.json());

export const getRenewalHistory = (networkHeaders: any) => (key: any, id: string) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${id}/renewal_history`, {
    method: "GET",
    headers: networkHeaders,
  }).then((res) => res.json());

export const putPermit = (networkHeaders: any, id: string) => (body: any) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${id}`, {
    method: "PUT",
    headers: networkHeaders,
    body: JSON.stringify({ data: { attributes: decamelizeKeys(body, "-") } }),
  });

export const putPermitWorkflows = (networkHeaders: any, id: string) => (body: any) => {
  const updateFee: boolean = !!body.updateFee;
  const queryParams = updateFee ? `?update_fee=${updateFee}` : "";
  return fetch(`${AppSettings.PERMITS_API_URL}/${id}/steps/${body.step_id}/${body.action}${queryParams}`, {
    method: "PUT",
    headers: networkHeaders,
    body: JSON.stringify({ data: { attributes: decamelizeKeys(body.data, "-") } }),
  });
};

export const deletePermit = (networkHeaders: any, id: any) => () =>
  fetch(`${AppSettings.PERMITS_API_URL}/${id}`, {
    method: "DELETE",
    headers: networkHeaders,
  }).then(async (res) => {
    if (res.ok) return res;
    if (!res || !res.status) {
      throw new Error("Unable to delete application");
    }
    const responseJson = await res.json();
    switch (res.status) {
      case 403:
        throw new Error("You do not have permission to delete this application");
      case 404:
        throw new Error("Application not found");
      case 500:
        throw new Error("We ran into an issue deleting this application");
      case 503:
        throw new Error("We ran into an issue deleting this application");
      default:
        throw new Error(`${responseJson.error}`);
    }
  });

export const postPermitCharges = (networkHeaders: any, id: string) => (body: any) => {
  const url = `${AppSettings.PERMITS_API_URL}/${id}/charges`;
  return fetch(url, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify({ data: body.map((element: any) => ({ attributes: decamelizeKeys(element, "-") })) }),
  });
};

export const getPermitCharges = (networkHeaders: any) => (key: any, id: string) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${id}/charges`, {
    method: "GET",
    headers: networkHeaders,
  }).then((res) => res.json());

export const getPermitAttachments = (networkHeaders: any) => (key: any, id: string) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${id}/attachments`, {
    method: "GET",
    headers: networkHeaders,
  }).then((res) => res.json());

export const postPermitAttachments = (networkHeaders: any, id: string) => (body: any) => {
  const url = `${AppSettings.PERMITS_API_URL}/${id}/attachments`;
  return fetch(url, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify({ data: body.map((element: any) => ({ attributes: decamelizeKeys(element, "-") })) }),
  });
};

export const deletePermitAttachment = (networkHeaders: any, id: string) => (attachmentId: number) => {
  const url = `${AppSettings.PERMITS_API_URL}/${id}/attachments/${attachmentId}`;
  return fetch(url, {
    method: "DELETE",
    headers: networkHeaders,
  });
};

export const postPermitCheckout = (networkHeaders: any, id: string, feeScheduleId?: string) => (body: any) => {
  const url = feeScheduleId
    ? `${AppSettings.PERMITS_API_URL}/${id}/checkout/${feeScheduleId}`
    : `${AppSettings.PERMITS_API_URL}/${id}/checkout`;
  return fetch(url, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify({ data: { attributes: decamelizeKeys(body, "-") } }),
  });
};

export const postConvertV1V2 = (networkHeaders: any) => (requestBody: any) => {
  const url = `${AppSettings.PERMITS_API_URL}/convert_v1_v2`;
  return fetch(url, {
    method: "POST",
    headers: networkHeaders,
    body: requestBody,
  });
};

export const postPermitCheckoutDemoStart = (networkHeaders: any, id: string) => (body: any) => {
  const url = `${AppSettings.PERMITS_API_URL}/${id}/checkout/demo/start`;
  return fetch(url, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify({ data: { attributes: decamelizeKeys(body, "-") } }),
  });
};

export const postPermitCheckoutDemoComplete = (networkHeaders: any, id: string) => (body: any) => {
  const url = `${AppSettings.PERMITS_API_URL}/${id}/checkout/demo/complete`;
  return fetch(url, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify({ data: { attributes: decamelizeKeys(body, "-") } }),
  });
};

export const postPermitPayment = (networkHeaders: any, id: any) => (body: any) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${id}/transactions`, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify({
      data: {
        attributes: {
          method: body.data.method,
          amount: new Number(body.data.amount),
          amount_received: new Number(body.data.amount),
          comment: body.data.comment,
          cash_register_id: body.data.cash_register_id != null ? body.data.cash_register_id : null,
        },
      },
    }),
  });

export const postPermitPendingPayment = (networkHeaders: any) => (id: number) => {
  return fetch(`${AppSettings.PERMITS_API_URL}/${id}/pending`, {
    method: "POST",
    headers: networkHeaders,
  });
};

export const postPermitChargesTransaction = (networkHeaders: any, id: string) => (body: any) => {
  return fetch(`${AppSettings.PERMITS_API_URL}/${id}/charges/transactions`, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify({ data: { attributes: decamelizeKeys(body, "-") } }),
  });
};

export const patchPermitTransaction = (networkHeaders: any, id: string, transactionId: string) => (body: any) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${id}/transactions/${transactionId}`, {
    method: "PATCH",
    headers: networkHeaders,
    body: JSON.stringify({ data: { attributes: decamelizeKeys(body, "-") } }),
  });

type StatusRoute = (typeof StatusRoutes)[keyof typeof StatusRoutes];
const StatusRoutes = {
  OPEN: "open",
  ON_HOLD: "hold",
  CLOSED: "close",
  COMPLETED: "complete",
  IN_PROGRESS: "in_progress",
} as const;

const StatusToStatusRoute: Record<Status, StatusRoute> = {
  open: StatusRoutes.OPEN,
  on_hold: StatusRoutes.ON_HOLD,
  closed: StatusRoutes.CLOSED,
  completed: StatusRoutes.COMPLETED,
  in_progress: StatusRoutes.IN_PROGRESS,
} as const;

export const putPermitStatus =
  (networkHeaders: any, id: string) =>
  ({ status, updateFees }: { status: Status; updateFees: boolean }) => {
    const queryParams = updateFees ? `?update_fee=${updateFees}` : "";
    const url = `${AppSettings.PERMITS_API_URL}/${id}/${StatusToStatusRoute[status]}${queryParams}`;
    return fetch(url, {
      method: "PUT",
      headers: networkHeaders,
    }).then((res) => {
      if (res.ok) return res;
      throw new Error(`Update failed with status ${res.status}`);
    });
  };

export const putPermitFormalNumber = (networkHeaders: any, id: string) => (body: any) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${id}/reservation_number`, {
    method: "PUT",
    headers: networkHeaders,
    body: JSON.stringify({ data: { attributes: body } }),
  }).then((res) => {
    if (res.ok) return res;
    throw new Error(`Update failed with status ${res.status}`);
  });

export const postFeeDifferences = (networkHeaders: any, id: string) => (body: any) => {
  return fetch(`${AppSettings.PERMITS_API_URL}/${id}/fee_differences`, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify({ data: { attributes: body } }),
  });
};

export const putFeeDifferences = (networkHeaders: any, id: string) => (body: any) => {
  return fetch(`${AppSettings.PERMITS_API_URL}/${id}?update_fee=true`, {
    method: "PUT",
    headers: networkHeaders,
    body: JSON.stringify({ data: { attributes: body } }),
  });
};

export const postPermitFormalNumber = (networkHeaders: any, id: string) => () =>
  fetch(`${AppSettings.PERMITS_API_URL}/${id}/reserve_number`, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify({}),
  }).then((res) => res.json());

export const getPermitFormalNumberPreview = (networkHeaders: any, permitId: string) => () =>
  fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/reservation_number/preview`, {
    method: "GET",
    headers: networkHeaders,
  }).then((res) => res.json());

export const postNotRenewing = (networkHeaders: any) => (body: any) => {
  return fetch(`${AppSettings.PERMITS_API_URL}/${body.id}/not_renewing`, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify({ data: { attributes: decamelizeKeys(body.data, "-") } }),
  });
};

export const getExportProgress = (networkHeaders: any) => (key: any, id: string) =>
  fetch(`${AppSettings.SEARCH_API_URL}/permits/export_csv/${id}`, {
    method: "GET",
    headers: networkHeaders,
  }).then((res) => res.json());

export const postExportCsv = (networkHeaders: any) => (searchParams: any) => {
  return fetch(`${AppSettings.SEARCH_API_URL}/permits/export_csv`, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify(Object.assign({}, searchParams)),
  });
};

export const postTransactionsExportCsv = (networkHeaders: any) => (searchParams: any) => {
  return fetch(`${AppSettings.SEARCH_API_URL}/transactions/export_csv`, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify(Object.assign({}, searchParams)),
  });
};

export const putPermitContact = (networkHeaders: any, id: number | undefined) => (body: any) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${id}/contact`, {
    method: "PUT",
    headers: networkHeaders,
    body: JSON.stringify({ data: { attributes: decamelizeKeysDeep(body) } }),
  }).then((res) => {
    if (res.ok) return res;
    throw new Error(`Query failed with status ${res.status}`);
  });

export const postPermitContact = (networkHeaders: any) => (body: any) =>
  fetch(`${AppSettings.PERMITS_API_URL}/contact`, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify({ data: { attributes: decamelizeKeysDeep(body) } }),
  }).then((res) => {
    if (res.ok) return res;
    throw new Error(`Query failed with status ${res.status}`);
  });

export const deletePermitContact = (networkHeaders: any, contactId: number) => () =>
  fetch(`${AppSettings.PERMITS_API_URL}/${contactId}/contact`, {
    method: "DELETE",
    headers: networkHeaders,
  }) // Make sure we handle errors
    .then((res) => {
      if (res.ok) return res;
      throw new Error(`Query failed with status ${res.status}`);
    });

export const postPermitContactInvite =
  (networkHeaders: any) =>
  ({ permitId, contactId, email }: { permitId: number; contactId: number; email?: string }) =>
    fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/contacts/${contactId}/invite`, {
      method: "POST",
      headers: networkHeaders,
      body: email ? JSON.stringify({ email }) : "",
    })
      // Make sure we handle errors
      .then((res) => {
        if (res.ok) return res;
        throw new Error(`Query failed with status ${res.status}`);
      })
      .then((res) => res.json());

export const postPermitContactRevokeInvite =
  (networkHeaders: any) =>
  ({ permitId, contactId }: { permitId: number; contactId: number }) =>
    fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/contacts/${contactId}/invite/revoke`, {
      method: "POST",
      headers: networkHeaders,
      body: "",
    })
      // Make sure we handle errors
      .then((res) => {
        if (res.ok) return res;
        throw new Error(`Query failed with status ${res.status}`);
      })
      .then((res) => res.json());

export const postPermitContactAcceptInvite =
  (networkHeaders: any) =>
  ({ permitId, contactId }: { permitId: number; contactId: number }) =>
    fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/contacts/${contactId}/invite/accept`, {
      method: "POST",
      headers: networkHeaders,
      body: "",
    })
      // Make sure we handle errors
      .then((res) => {
        if (res.ok) return res;
        throw new Error(`Query failed with status ${res.status}`);
      })
      .then((res) => res.json());

export const postPermitContactAcceptInviteAndConnect =
  (networkHeaders: any, permitId: string, contactId: string) => (body: { data: { attributes: { token: string } } }) =>
    fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/contacts/${contactId}/invite/accept_and_connect`, {
      method: "POST",
      headers: networkHeaders,
      body: JSON.stringify(Object.assign({}, body)),
    })
      // Make sure we handle errors
      .then(async (res) => {
        if (res.ok) return res;
        const resJson = await res.json();
        throw new Error(resJson?.error);
      })
      .then((res) => res.json());

export const postPermitContactInactive =
  (networkHeaders: any, permitId: number | string, contactId: number | string) => () =>
    fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/contacts/${contactId}/set_inactive`, {
      method: "POST",
      headers: networkHeaders,
    })
      // Make sure we handle errors
      .then((res) => {
        if (res.ok) return res;
        throw new Error(`Query failed with status ${res.status}`);
      });

export const postPermitContactActive =
  (networkHeaders: any, permitId: number | string, contactId: number | string) => () =>
    fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/contacts/${contactId}/set_active`, {
      method: "POST",
      headers: networkHeaders,
    })
      // Make sure we handle errors
      .then((res) => {
        if (res.ok) return res;
        throw new Error(`Query failed with status ${res.status}`);
      });

export const postPermitContactConnectUser =
  (networkHeaders: any, permitId: number | string, contactId: number | string) => (body: any) =>
    fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/contacts/${contactId}/connect_user`, {
      method: "POST",
      headers: networkHeaders,
      body: JSON.stringify({ data: { attributes: decamelizeKeysDeep(body) } }),
    })
      // Make sure we handle errors
      .then(async (res) => {
        if (res.ok) return res;
        const resJson = await res.json();
        throw new Error(resJson?.error);
      });

export const postPermitContactDisconnectUser =
  (networkHeaders: any, permitId: number | string, contactId: number | string) => () =>
    fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/contacts/${contactId}/disconnect_user`, {
      method: "POST",
      headers: networkHeaders,
    })
      // Make sure we handle errors
      .then((res) => {
        if (res.ok) return res;
        throw new Error(`Query failed with status ${res.status}`);
      });

export const getPermitUserPermissions = (networkHeaders: any, permitId: number | string) => () =>
  fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/get_permissions`, {
    method: "GET",
    headers: networkHeaders,
  }).then((res) => res.json());

export const postPermitContactIgnoreDiscrepancies =
  (networkHeaders: any, permitId: number | string, contactId: number | string) => () =>
    fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/contacts/${contactId}/ignore_discrepancies`, {
      method: "POST",
      headers: networkHeaders,
    })
      // Make sure we handle errors
      .then(async (res) => {
        if (res.ok) return res;
        const resJson = await res.json();
        throw new Error(resJson?.error);
      });

export const postPermitPrimaryLocationIgnoreDiscrepancies = (networkHeaders: any, permitId: number | string) => () =>
  fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/primary_location/ignore_discrepancies`, {
    method: "POST",
    headers: networkHeaders,
  })
    // Make sure we handle errors
    .then(async (res) => {
      if (res.ok) return res;
      const resJson = await res.json();
      throw new Error(resJson?.error);
    });

export const postPermitRefund = (networkHeaders: any, permitId: any) => (body: any) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/refund`, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify({
      data: {
        attributes: {
          method: body.data.method,
          amount: new Number(body.data.amount),
          comment: body.data.comment,
          cash_register_id: body.data.cash_register_id != null ? body.data.cash_register_id : null,
        },
      },
    }),
  });

export const postPermitStripeRefund = (networkHeaders: any, permitId: any) => (body: any) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/refund`, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify({
      data: {
        attributes: {
          payment_id: new Number(body.data.payment[0]),
          method: body.data.method,
          amount: new Number(body.data.amount),
          comment: body.data.comment,
        },
      },
    }),
  });

export const postPermitWaive = (networkHeaders: any, id: any) => (body: any) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${id}/waive_fee`, {
    method: "POST",
    headers: networkHeaders,
    body: JSON.stringify({
      data: {
        attributes: {
          fee_id: body.data.fee_id,
          amount: new Number(body.data.amount),
        },
      },
    }),
  });

export const patchPermitPayment = (networkHeaders: any, transactionId: any) => (body: any) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${transactionId}/transaction`, {
    method: "PATCH",
    headers: networkHeaders,
    body: JSON.stringify({
      data: {
        attributes: {
          method: body.method,
          comment: body.comment,
          cash_register_id: body.cash_register_id,
          main_amount: body.main_amount,
          amount: body.amount,
          confirmed_at: body.confirmed_at,
        },
      },
    }),
  });

export const postPermitConnectRenewalPermit =
  (networkHeaders: any, id: number | string) => (childPermitId: string | number) =>
    fetch(`${AppSettings.PERMITS_API_URL}/${id}/connect_renewal_permit`, {
      method: "POST",
      headers: networkHeaders,
      body: JSON.stringify({ data: { attributes: { child_permit_id: childPermitId } } }),
    }).then((res) => {
      if (res.ok) return res;
      if (!res || !res.status) throw new Error(`Unable to connect renewal permit`);
      if (res.status === 404) throw new Error(`Permit not found`);
      if (res.status === 400 || res.status == 403) {
        return res.text().then((text) => {
          throw new Error(JSON.parse(text).error);
        });
      }
      if (res.status >= 500) throw new Error(`Server error`);
    });

export const getPermitDocumentPreview = (networkHeaders: any, id: number) => (documentTemplateId: string) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${id}/documents/${documentTemplateId}/preview`, {
    method: "GET",
    headers: networkHeaders,
  }).then((res) => res.json());

export const getPermitDocumentContext = (networkHeaders: any) => (permitId: string) =>
  fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/documents/context`, {
    method: "GET",
    headers: networkHeaders,
  })
    .then((res) => {
      if (res.ok) return res;
      throw new Error(`Query failed with status ${res.status}`);
    })
    .then((res) => res.json());

export const postPermitStepDocumentsReIssue =
  (networkHeaders: any) =>
  ({ permitId, stepId }: { permitId: string; stepId: string }) =>
    fetch(`${AppSettings.PERMITS_API_URL}/${permitId}/steps/${stepId}/documents/re-issue`, {
      method: "POST",
      headers: networkHeaders,
    })
      .then((res) => {
        if (res.ok) return res;
        throw new Error(`Query failed with status ${res.status}`);
      })
      .then((res) => res.json());
